import {
  FirstLevelEntity,
  SecondLevelEntity,
  Shipment,
} from "./shipment.interface";
import { publicAxios } from "../../config/axios";
import { convertToQueryString } from "../../utils/convertQuery";
import endpoints from "../../utils/endpoints";

// New function to get first level entities based on grouping and status filter
export const getFirstLevelEntities = async (
  grouping: "customer" | "supplier",
  statusFilter: "undelivered" | "delivered" | "cancelled" = "undelivered"
): Promise<FirstLevelEntity[]> => {
  try {
    const groupBy = grouping === "customer" ? "customers" : "suppliers";
    let statusParam: string;

    if (statusFilter === "delivered") {
      statusParam = "includeStatus=DELIVERED";
    } else if (statusFilter === "cancelled") {
      statusParam = "includeStatus=CANCELLED";
    } else {
      statusParam = "excludeStatus=DELIVERED&excludeStatus=CANCELLED";
    }

    const response = await publicAxios({
      url: `/v1/shipments?groupBy=${groupBy}&${statusParam}`,
      method: "GET",
      headers: {
        Authorization: localStorage.getItem("auth_orca"),
      },
    });

    // Transform the response to match FirstLevelEntity interface
    const entities: FirstLevelEntity[] = response.data?.map((item: any) => {
      if (grouping === "customer") {
        const customer = item.customer[0];
        const addresses = customer.addresses?.[0];

        return {
          _id: customer._id,
          name: customer.name,
          type: customer.type,
          totalShipmentsCount: item.totalShipments,
          totalUndeliveredShipments: item.totalUndeliveredShipments,
          inTransitShipmentsCount: 0,
          cancelledShipmentsCount:
            statusFilter === "cancelled" ? item.totalShipments : 0,
          totalDeliveredShipments:
            statusFilter === "delivered"
              ? item.totalShipments
              : statusFilter === "cancelled"
              ? 0
              : item.totalShipments - (item.totalUndeliveredShipments || 0),
          engagementMode: customer.engagementMode,
          address: {
            addressLine1: addresses.addressLine1,
            addressLine2: addresses?.addressLine2 || "",
            city: addresses.city,
            state: addresses.state,
            pinCode: addresses.pinCode,
            country: addresses.country || "India",
            location: {
              latitude:
                typeof addresses.location === "object" &&
                addresses.location?.latitude
                  ? addresses.location.latitude
                  : 0,
              longitude:
                typeof addresses.location === "object" &&
                addresses.location?.longitude
                  ? addresses.location.longitude
                  : 0,
            },
          },
        };
      } else {
        const supplier = item.supplier[0];

        return {
          _id: supplier._id,
          name: supplier.businessName || supplier.name,
          type: "supplier",
          totalShipmentsCount: item.totalShipments,
          totalUndeliveredShipments: item.totalUndeliveredShipments,
          inTransitShipmentsCount: 0,
          cancelledShipmentsCount:
            statusFilter === "cancelled" ? item.totalShipments : 0,
          totalDeliveredShipments:
            statusFilter === "delivered"
              ? item.totalShipments
              : statusFilter === "cancelled"
              ? 0
              : item.totalShipments - (item.totalUndeliveredShipments || 0),
          address: {
            addressLine1: supplier.address?.addressLine1,
            addressLine2: supplier.address?.addressLine2,
            city: supplier.address?.city,
            state: supplier.address?.state,
            pinCode: supplier.address?.pinCode,
            country: supplier.address?.country,
            location: {
              latitude: supplier.address?.location?.latitude || 0,
              longitude: supplier.address?.location?.longitude || 0,
            },
          },
        };
      }
    });

    return entities;
  } catch (error) {
    console.error(`Error in getFirstLevelEntities (${grouping}):`, error);
    return [];
  }
};

// New function to get second level entities based on grouping and status filter
export const getSecondLevelEntities = async (
  firstLevelId: string,
  grouping: "customer" | "supplier",
  statusFilter: "undelivered" | "delivered" | "cancelled" = "undelivered"
): Promise<SecondLevelEntity[]> => {
  try {
    const groupBy = grouping === "customer" ? "suppliers" : "customers";
    const paramName = grouping === "customer" ? "customerId" : "supplierId";
    let statusParam: string;

    if (statusFilter === "delivered") {
      statusParam = "includeStatus=DELIVERED";
    } else if (statusFilter === "cancelled") {
      statusParam = "includeStatus=CANCELLED";
    } else {
      statusParam = "excludeStatus=DELIVERED&excludeStatus=CANCELLED";
    }

    const response = await publicAxios({
      url: `/v1/shipments?${paramName}=${firstLevelId}&groupBy=${groupBy}&${statusParam}`,
      method: "GET",
      headers: {
        Authorization: localStorage.getItem("auth_orca"),
      },
    });

    // Transform the response to match SecondLevelEntity interface
    const entities: SecondLevelEntity[] = response.data?.map((item: any) => {
      if (grouping === "customer") {
        const supplier = item.supplier[0];

        return {
          _id: supplier._id,
          name: supplier.businessName || supplier.name,
          totalShipmentsCount: item.totalShipments,
          totalUndeliveredShipments: item.totalUndeliveredShipments,
          inTransitShipmentsCount: 0,
          cancelledShipmentsCount:
            statusFilter === "cancelled" ? item.totalShipments : 0,
          totalDeliveredShipments:
            statusFilter === "delivered"
              ? item.totalShipments
              : statusFilter === "cancelled"
              ? 0
              : item.totalShipments - (item.totalUndeliveredShipments || 0),
          address: {
            addressLine1: supplier.address?.addressLine1,
            addressLine2: supplier.address?.addressLine2,
            city: supplier.address?.city,
            state: supplier.address?.state,
            pinCode: supplier.address?.pinCode,
            country: supplier.address?.country,
            location: {
              latitude: supplier.address?.location?.latitude || 0,
              longitude: supplier.address?.location?.longitude || 0,
            },
          },
        };
      } else {
        const customer = item.customer[0];
        const addresses = customer.addresses?.[0];

        return {
          _id: customer._id,
          name: customer.name,
          totalShipmentsCount: item.totalShipments,
          totalUndeliveredShipments: item.totalUndeliveredShipments,
          inTransitShipmentsCount: 0,
          cancelledShipmentsCount:
            statusFilter === "cancelled" ? item.totalShipments : 0,
          totalDeliveredShipments:
            statusFilter === "delivered"
              ? item.totalShipments
              : item.totalShipments - (item.totalUndeliveredShipments || 0),
          engagementMode: customer.engagementMode,
          address: {
            addressLine1: addresses.addressLine1,
            addressLine2: addresses?.addressLine2 || "",
            city: addresses.city,
            state: addresses.state,
            pinCode: addresses.pinCode,
            country: addresses.country || "India",
            location: {
              latitude:
                typeof addresses.location === "object" &&
                addresses.location?.latitude
                  ? addresses.location.latitude
                  : 0,
              longitude:
                typeof addresses.location === "object" &&
                addresses.location?.longitude
                  ? addresses.location.longitude
                  : 0,
            },
          },
        };
      }
    });

    return entities;
  } catch (error) {
    console.error(`Error in getSecondLevelEntities (${grouping}):`, error);
    return [];
  }
};

// Updated getShipments function to work with both grouping approaches and status filter
export const getShipments = async (
  firstLevelId: string,
  secondLevelId: string,
  grouping: "customer" | "supplier",
  statusFilter: "undelivered" | "delivered" | "cancelled" = "undelivered"
): Promise<Shipment[]> => {
  try {
    let statusParam: string;

    if (statusFilter === "delivered") {
      statusParam = "includeStatus=DELIVERED";
    } else if (statusFilter === "cancelled") {
      statusParam = "includeStatus=CANCELLED";
    } else {
      statusParam = "excludeStatus=DELIVERED&excludeStatus=CANCELLED";
    }

    let url = `/v1/shipments?${statusParam}`;

    if (grouping === "customer") {
      url += `&customerId=${firstLevelId}&supplierId=${secondLevelId}`;
    } else {
      url += `&supplierId=${firstLevelId}&customerId=${secondLevelId}`;
    }

    const response = await publicAxios({
      url,
      method: "GET",
      headers: {
        Authorization: localStorage.getItem("auth_orca"),
      },
    });

    return response.data as unknown as Shipment[];
  } catch (error) {
    console.error("Error fetching shipments:", error);
    return [];
  }
};

export const getShipment = async (
  shipmentId: string,
  params?: any
): Promise<Shipment> => {
  try {
    const queryString = convertToQueryString(params);
    const response = await publicAxios({
      url: `/v1/shipments/${shipmentId}?${queryString}`,
      method: "GET",
      headers: {
        Authorization: localStorage.getItem("auth_orca"),
      },
    });
    return response.data;
  } catch (error: any) {
    console.error("Error fetching shipment:", error);
    throw error.response.data;
  }
};

export const getTracking = async (shipmentId: string) => {
  try {
    const baseUrl = process.env.REACT_APP_BASE_URL?.replace(/\/api$/, "");
    const response = await fetch(
      `${baseUrl}/v1/porter/shipments/${shipmentId}/track`,
      {
        method: "GET",
        headers: {
          Authorization: localStorage.getItem("auth_orca") || "",
        },
      }
    );
    return response.json();
  } catch (error) {
    console.error("Failed to update tracking for shipment:", shipmentId, error);
  }
};

export const cancelShipment = async (shipmentId: string): Promise<any> => {
  try {
    const response = await publicAxios({
      url: `/v1/shipments/${shipmentId}/cancel`,
      method: "POST",
      headers: {
        Authorization: localStorage.getItem("auth_orca") || "",
      },
    });
    return response.data;
  } catch (error: any) {
    const errorMessage =
      error?.response?.data?.errorMsg ||
      error?.response?.data?.message ||
      error?.response?.data?.error ||
      error?.message ||
      "Failed to cancel shipment. Please try again.";
    console.error("Error cancelling shipment:", errorMessage);
    throw new Error(errorMessage);
  }
};

export const deleteShipment = async (shipmentId: string): Promise<any> => {
  try {
    const response = await publicAxios({
      url: `/v1/shipments/${shipmentId}`,
      method: "DELETE",
      headers: {
        Authorization: localStorage.getItem("auth_orca"),
      },
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const updateShipmentService = async (
  shipmentId: string,
  payload: any
): Promise<any> => {
  try {
    const response = await publicAxios({
      url: endpoints.updateShipment(shipmentId),
      method: "PATCH",
      headers: {
        Authorization: localStorage.getItem("auth_orca"),
        "Content-Type": "application/json",
      },
      data: payload,
    });
    return response.data;
  } catch (error: any) {
    const errorMessage =
      error?.response?.data?.errorMsg ||
      error?.response?.data?.message ||
      error?.response?.data?.error ||
      error?.message ||
      "Failed to update shipment status. Please try again.";

    console.error("Error updating shipment status:", errorMessage);
    throw new Error(errorMessage);
  }
};

export const DELIVERY_OTP_REQUIRED = "DELIVERY_OTP_REQUIRED";

export class MarkConsignmentDeliveredError extends Error {
  readonly errorCode?: string;

  constructor(message: string, errorCode?: string) {
    super(message);
    this.name = "MarkConsignmentDeliveredError";
    this.errorCode = errorCode;
  }
}

export const markConsignmentDeliveredService = async (
  shipmentId: string,
  orderNo: string,
  deliveryOtp?: string
): Promise<any> => {
  try {
    const response = await publicAxios({
      url: `/v1/shipments/${shipmentId}/consignments/${orderNo}/mark-delivered`,
      method: "POST",
      headers: {
        Authorization: localStorage.getItem("auth_orca"),
        "Content-Type": "application/json",
      },
      ...(deliveryOtp
        ? { data: { deliveryOtp } }
        : {}),
    });
    return response.data;
  } catch (error: any) {
    const errorMessage =
      error?.response?.data?.errorMsg ||
      error?.response?.data?.message ||
      error?.response?.data?.error ||
      error?.message ||
      "Failed to mark consignment as delivered.";
    const errorCode =
      typeof error?.response?.data?.errorCode === "string"
        ? error.response.data.errorCode
        : undefined;

    throw new MarkConsignmentDeliveredError(errorMessage, errorCode);
  }
};
